/**
 * RTK Query API Slice
 * 统一的API请求和缓存管理
 */

import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { tokenManager } from '../../utils/tokenManager';

// API基础配置
console.log('API配置初始化', {
  REACT_APP_API_URL: process.env.REACT_APP_API_URL,
  baseUrl: process.env.REACT_APP_API_URL || '/api/v1',
});

const baseQuery = fetchBaseQuery({
  baseUrl: '/api/v1', // 使用相对路径，通过代理转发
  // credentials: 'include', // 暂时移除，等认证系统完成后再启用

  // 请求预处理 - 统一认证头管理
  prepareHeaders: (headers, { endpoint, getState, extra, ...context }) => {
    // 登录相关的API不需要认证头 (endpoint是RTK Query的endpoint名称)
    const publicEndpoints = [
      'login',
      'getBuiltInUsers',
      'autoRegisterWithQuestionnaire',
    ];
    const isPublicEndpoint = publicEndpoints.includes(endpoint);

    console.log('API请求预处理', {
      endpoint,
      isPublicEndpoint,
      headers: headers.get('content-type'),
    });

    if (!isPublicEndpoint) {
      // 使用统一的token管理器获取token
      const token = tokenManager.getToken();
      const authInfo = tokenManager.getAuthInfo();

      if (token) {
        headers.set('authorization', `Bearer ${token}`);
        console.log('API请求添加认证头 (统一管理)', {
          endpoint,
          userRole: authInfo.user?.role,
          userName: authInfo.user?.name,
          isAuthenticated: authInfo.isAuthenticated,
          rememberMe: authInfo.rememberMe,
        });
      } else {
        console.warn('API请求缺少认证token - 统一管理器未找到有效token', {
          endpoint,
        });
        // 输出调试信息
        console.debug('Token调试信息:', tokenManager.getDebugInfo());
      }
    } else {
      console.log('公开API端点，跳过认证头', { endpoint });
    }

    // CSRF 防护 - 添加 CSRF Token
    // 使用 Double-Submit Cookie 模式：从 cookie 读取 token 并添加到请求头
    const csrfToken = document.cookie
      .split('; ')
      .find(row => row.startsWith('XSRF-TOKEN='))
      ?.split('=')[1];

    if (csrfToken) {
      headers.set('X-CSRF-Token', decodeURIComponent(csrfToken));
    }

    // 添加自定义头以帮助服务器识别 AJAX 请求（额外的 CSRF 防护层）
    headers.set('X-Requested-With', 'XMLHttpRequest');

    // 设置内容类型
    if (!headers.get('content-type')) {
      headers.set('content-type', 'application/json');
    }

    return headers;
  },
});

// 错误类型定义
const ErrorTypes = {
  AUTH_EXPIRED: 'AUTH_EXPIRED',
  AUTH_INVALID: 'AUTH_INVALID',
  AUTH_MISSING: 'AUTH_MISSING',
  PERMISSION_DENIED: 'PERMISSION_DENIED',
  SERVER_ERROR: 'SERVER_ERROR',
};

// 带智能错误处理的基础查询
const baseQueryWithReauth = async (args: any, api: any, extraOptions: any) => {
  let result = await baseQuery(args, api, extraOptions);

  // 智能401错误处理
  if (result.error && result.error.status === 401) {
    const errorData = result.error.data as any;
    const errorType = errorData?.error?.type;
    const endpoint = args.url?.split('/').pop() || 'unknown';

    // 跳过登录API的401错误
    if (args.url?.includes('/auth/login')) {
      console.log('登录API返回401，这是正常的登录失败');
      return result;
    }

    console.warn('收到401错误', {
      endpoint,
      errorType,
      errorMessage: errorData?.error?.message,
    });

    // 只有真正的认证错误才触发重新登录
    const authErrors = [
      ErrorTypes.AUTH_EXPIRED,
      ErrorTypes.AUTH_INVALID,
      ErrorTypes.AUTH_MISSING,
    ];

    if (authErrors.includes(errorType)) {
      console.warn('认证错误，需要重新登录', { errorType, endpoint });

      // 获取当前认证信息
      const authInfo = tokenManager.getAuthInfo();
      console.log('当前认证状态:', authInfo);

      // 清除所有token信息
      tokenManager.clearAll();

      // 清除Redux状态
      api.dispatch({ type: 'auth/logout' });

      // 智能重定向
      if (authInfo.user && authInfo.user.email) {
        console.error(`认证已过期，请重新登录 (${authInfo.user.email})`);

        if (typeof window !== 'undefined') {
          // 保存当前路径，登录后可以回到这里
          const currentPath = window.location.pathname + window.location.search;
          localStorage.setItem('redirectAfterLogin', currentPath);

          // 延迟重定向，给用户时间看到提示
          setTimeout(() => {
            window.location.href = '/auth/login';
          }, 3000);
        }
      }
    } else if (errorType === ErrorTypes.PERMISSION_DENIED) {
      // 权限不足，不需要登出，只显示错误信息
      console.warn('权限不足，但不需要重新登录', { endpoint });
      // 不触发登出，让组件自己处理错误显示
    } else {
      // 其他401错误，可能是临时问题
      console.warn('未分类的401错误，可能是临时问题', {
        endpoint,
        errorType,
        fullError: errorData,
      });

      // 不立即登出，给用户机会重试
      // 只有连续多次401错误才考虑登出
      const failureKey = `auth_failure_${endpoint}`;
      const failureCount =
        parseInt(sessionStorage.getItem(failureKey) || '0') + 1;
      sessionStorage.setItem(failureKey, failureCount.toString());

      if (failureCount >= 3) {
        console.error('连续认证失败，强制重新登录');
        tokenManager.clearAll();
        api.dispatch({ type: 'auth/logout' });

        if (typeof window !== 'undefined') {
          setTimeout(() => {
            window.location.href = '/auth/login';
          }, 1000);
        }
      }
    }
  } else {
    // 成功的请求，清除失败计数
    const endpoint = args.url?.split('/').pop() || 'unknown';
    const failureKey = `auth_failure_${endpoint}`;
    sessionStorage.removeItem(failureKey);
  }

  return result;
};

// 创建API slice
export const apiSlice = createApi({
  reducerPath: 'api',
  baseQuery: baseQueryWithReauth,

  // 标签类型定义 - 用于缓存失效
  tagTypes: [
    // 基础类型
    'User',
    'Questionnaire',
    'Project',
    'Screening',
    'Health',
    'Channel',
    // Changelog 相关
    'Changelog',
    'ChangelogStatistics',
    'ChangelogTimeline',
    // 评估相关
    'AssessmentDocument',
    'AssessmentDocuments',
    'AssessmentModule',
    'AssessmentReport',
    'DetailedAssessment',
    // 审核相关
    'ReviewHistory',
    'ReviewStatistics',
    // 尽调相关
    'DDDocument',
    'DDProgress',
    'DDReport',
    // SPO 相关
    'SPOProcess',
    'SPOList',
    'EmailList',
    // 文档智能相关
    'DocumentParsing',
    'DocumentList',
    'ParsedData',
    'FieldLocation',
    'DataValidation',
    // 项目相关
    'IssuerProject',
    'ProjectDetail',
    'ProjectWorkflow',
    // 评级认证相关
    'RatingCertification',
    // 第三方服务
    'TianyanchaStatus',
    'RiskAssessment',
  ],

  // API端点定义
  endpoints: builder => ({
    // 健康检查
    getHealth: builder.query<HealthResponse, void>({
      query: () => '/health',
      providesTags: ['Health'],
    }),

    // API状态检查
    getApiStatus: builder.query<ApiStatusResponse, void>({
      query: () => '/api/status',
    }),

    // 测试端点
    testApi: builder.mutation<TestResponse, TestRequest>({
      query: data => ({
        url: '/api/test',
        method: 'POST',
        body: data,
      }),
    }),
  }),
});

// 导出自动生成的hooks
export const { useGetHealthQuery, useGetApiStatusQuery, useTestApiMutation } =
  apiSlice;

// TypeScript类型定义
export interface ApiResponse<T = any> {
  success: boolean;
  message: string;
  data?: T;
  error?: string;
  meta?: {
    requestId: string;
    version: string;
    timestamp: string;
  };
}

export interface HealthResponse extends ApiResponse {
  data: {
    status: 'healthy' | 'unhealthy';
    timestamp: string;
    uptime: number;
    environment: string;
    version: string;
  };
}

export interface ApiStatusResponse extends ApiResponse {
  data: {
    endpoints: string[];
  };
}

export interface TestRequest {
  message: string;
  timestamp?: string;
  [key: string]: any;
}

export interface TestResponse extends ApiResponse {
  data: {
    received: TestRequest;
    timestamp: string;
  };
}
